<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
            <span class="material-icons-round">calculate</span>
          </span>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Trip Finance Detail</h2>
            <p class="text-sm text-gray-500">Financial breakdown for this trip.</p>
          </div>
        </div>
        <back-button
          class="inline-flex items-center h-10 gap-2 px-4 text-sm font-semibold text-gray-600 bg-white border border-gray-300 shadow-sm rounded-xl transition-colors duration-150 hover:bg-gray-50 hover:text-gray-800"
        >
          <i class="text-base material-icons-round">arrow_back</i>
          Go Back
        </back-button>
      </div>

      <!-- Loading -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center py-24 text-gray-400"
      >
        <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
        <span class="mt-3 text-sm font-medium text-gray-500">Loading finance detail...</span>
      </div>

      <!-- Empty -->
      <div
        v-else-if="!trip"
        class="flex flex-col items-center py-24 text-gray-400"
      >
        <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
          <i class="text-3xl material-icons-round">inbox</i>
        </span>
        <span class="text-sm font-medium text-gray-500">No finance detail found</span>
      </div>

      <!-- Breakdown -->
      <div v-else class="max-w-md mx-auto">
        <div class="elevated-card overflow-hidden bg-white border border-gray-200 rounded-2xl">
          <!-- Route summary -->
          <div class="p-5 bg-gradient-to-br from-taxiye-50 to-white border-b border-gray-100">
            <span
              class="inline-flex px-2.5 py-1 text-xs font-semibold tracking-widest uppercase rounded-md bg-taxiye-100 text-taxiye-600"
            >
              {{ trip.type }} Trip
            </span>
            <div class="flex mt-4">
              <div class="flex flex-col items-center mr-3">
                <i class="text-base leading-none text-green-500 material-icons-round">my_location</i>
                <span class="flex-1 w-px my-1 border-l border-gray-300 border-dashed"></span>
                <i class="text-base leading-none text-red-500 material-icons-round">flag</i>
              </div>
              <div class="flex flex-col justify-between min-w-0 space-y-2">
                <p class="text-sm text-gray-700 truncate">
                  {{ trip.pickUpAddress ? trip.pickUpAddress.name : "—" }}
                </p>
                <p class="text-sm text-gray-700 truncate">
                  {{ trip.dropOffAddress ? trip.dropOffAddress.name : "—" }}
                </p>
              </div>
            </div>
          </div>

          <!-- Line items -->
          <dl class="px-5 divide-y divide-gray-100">
            <div class="flex items-baseline justify-between py-3">
              <dt class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Fare</dt>
              <dd class="text-sm font-semibold text-gray-900">
                {{ money(trip.fare) }} <span class="text-xs font-medium text-gray-400">ETB</span>
              </dd>
            </div>
            <div class="flex items-baseline justify-between py-3">
              <dt class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Passenger Discount</dt>
              <dd class="text-sm font-semibold" :class="trip.discount ? 'text-red-600' : 'text-gray-900'">
                {{ trip.discount ? "-" : "" }}{{ money(trip.discount) }}
                <span class="text-xs font-medium text-gray-400">ETB</span>
              </dd>
            </div>
            <div class="flex items-baseline justify-between py-3">
              <dt class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Final Fare</dt>
              <dd class="text-sm font-semibold text-gray-900">
                {{ money(Number(trip.fare || 0) - Number(trip.discount || 0)) }}
                <span class="text-xs font-medium text-gray-400">ETB</span>
              </dd>
            </div>
            <div class="flex items-baseline justify-between py-3">
              <dt class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Commission</dt>
              <dd class="text-sm font-semibold text-gray-900">
                {{ money(trip.companyCut) }} <span class="text-xs font-medium text-gray-400">ETB</span>
              </dd>
            </div>
            <div class="flex items-baseline justify-between py-3">
              <dt class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Hard-working Discount</dt>
              <dd class="text-sm font-semibold text-gray-900">
                0.00 <span class="text-xs font-medium text-gray-400">ETB</span>
              </dd>
            </div>
            <div class="flex items-baseline justify-between py-3">
              <dt class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Final Commission</dt>
              <dd class="text-right">
                <div class="text-sm font-semibold text-gray-900">
                  {{ money(trip.net) }} <span class="text-xs font-medium text-gray-400">ETB</span>
                </div>
                <div class="text-xs text-gray-400">+ VAT {{ money(trip.tax) }}</div>
              </dd>
            </div>
          </dl>

          <!-- Total -->
          <div class="flex items-center justify-between px-5 py-4 bg-gray-50 border-t border-gray-100">
            <span class="text-xs font-bold tracking-widest text-gray-600 uppercase">Total</span>
            <span class="text-xl font-black leading-none text-gray-900">
              {{ money(trip.companyCut) }} <span class="text-xs font-semibold text-gray-400">ETB</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BackButton from '../../components/BackButton.vue';

export default {
  data() {
    return {
      trip: null,
      loading: true,
      totalDistance: 0,
      serverTimeDifference: 0,
      confirmOpen: false,
      map: null,
    };
  },
  components: {
    BackButton
  },
  watch: {},
  created() {
    this.getServerTime();
    this.getRide();
    this.$misc
      .get("/setting")
      .then((response) => {
        // this.settings = response.data
        console.log(response);
      })
      .catch((err) => console.log(err));
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    estimatedDistance() {
      return this.trip && this.trip.route && this.trip.route.distance
        ? this.trip.route.distance / 1000
        : 0;
    },
    estimatedDuration() {
      return this.trip && this.trip.route && this.trip.route.duration
        ? this.trip.route.duration / 60
        : 0;
    },
    tripDuration() {
      var tsts = new Date(this.trip.pickupTimestamp);
      // var date = new Date(new Date().getTime() - this.serverTimeDifference);
      var date = new Date(
        this.trip?.endTimestamp
          ? this.trip.endTimestamp
          : new Date().getTime() - this.serverTimeDifference
      );
      return (date.getTime() - tsts.getTime()) / 1000 / 60;
    },
    tripDistance() {
      return this.totalTripDistance();
    },
    tripFare() {
      var distance = this.tripDistance;
      var duration = this.tripDuration;
      if (distance && this.trip && this.trip.vehicleType && duration) {
        return (
          distance * this.trip.vehicleType.pricePerKM +
          duration * this.trip.vehicleType.pricePerMin +
          this.trip.vehicleType.baseFare
        );
      } else {
        return 0;
      }
    },
    estimatedFare() {
      var distance = this.estimatedDistance;
      var duration = this.estimatedDuration;
      if (distance && this.trip && this.trip.vehicleType && duration) {
        return (
          distance * this.trip.vehicleType.pricePerKM +
          duration * this.trip.vehicleType.pricePerMin +
          this.trip.vehicleType.baseFare
        );
      } else {
        return 0;
      }
    },
  },
  async mounted() {},
  methods: {
    money(amount) {
      return (Number(amount) || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    calculateDistance(from, to) {
      if (from.length == 2 && to.length == 2) {
        var R = 6371; // Radius of the earth in km
        var dLat = (Math.PI / 180) * (to[1] - from[1]); // (Math.PI / 180) *  below
        var dLon = (Math.PI / 180) * (to[0] - from[0]);
        var a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos((Math.PI / 180) * from[1]) *
            Math.cos((Math.PI / 180) * to[1]) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        var d = R * c; // Distance in km
        return d;
      } else {
        return 0;
      }
    },
    getServerTime() {
      this.$misc.get("/date").then((response) => {
        var date = new Date(response.data);
        this.serverTimeDifference = new Date().getTime() - date.getTime();
      });
    },
    openConfirmBox() {
      this.confirmOpen = true;
    },
    closeConfirmBox() {
      this.confirmOpen = false;
    },
    sendEmail() {
      this.loading = true;
      this.$misc
        .get(`trips/${this.id}/send-email`)
        .then((response) => {
          console.log({ response });
          this.loading = false;
        })
        .catch((error) => console.log({ error }))
        .finally(() => (this.loading = false));
    },
    cancelTrip() {
      this.loading = true;
      this.$misc
        .post(`trips/${this.id}/cancel`)
        .then((response) => {
          console.log({ response });
          this.trip = response.data;
          this.loading = false;
        })
        .catch((error) => console.log({ error }))
        .finally(() => (this.loading = false));
    },
    totalTripDistance() {
      var distance = 0;
      if (this.trip && this.trip.path && this.trip.path.length) {
        for (let i = 0; i < this.trip.path.length; i++) {
          if (i + 1 < this.trip.path.length) {
            distance += this.calculateDistance(
              this.trip.path[i],
              this.trip.path[i + 1]
            );
          }
        }
        this.totalDistance = distance.toFixed(2);
        return distance;
      } else {
        return distance;
      }
    },
    resendEmail() {
      this.loading = true;
      this.$misc
        .get(`trips/${this.id}/resend-email`)
        .then((response) => {
          console.log({ response });
          this.getRide();
        })
        .catch((error) => console.log({ error }))
        .finally(() => (this.loading = false));
    },
    endTrip() {
      try {
        var distance = parseFloat(this.totalDistance.toString());

        this.loading = true;
        this.$misc
          .post(`trips/${this.id}/end`, { totalDistance: distance })
          .then((response) => {
            console.log({ response });
            this.trip = response.data;
            this.loading = false;
          })
          .catch((error) => console.log({ error }))
          .finally(() => (this.loading = false));
      } catch (error) {
        console.log({ error });
      }
    },
    getRide() {
      this.loading = true;
      var path = `trips/${this.id}?limit=populate=["driver","passenger","vehicleType","dispatcher"]&start=${this.startDate}&end=${this.endDate}`;
      this.$misc
        .get(path)
        .then((response) => {
          this.trip = response.data;
          console.log(this.trip);
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
/* Cards — elevated at rest, matching the Finance index's warm layered shadow */
.elevated-card {
  box-shadow: 0 2px 4px rgba(77, 50, 0, 0.04), 0 12px 24px -8px rgba(77, 50, 0, 0.12);
}

.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
